import { $isListItemNode, ListItemNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  DELETE_CHARACTER_COMMAND,
  ElementNode,
  Point,
  RangeSelection,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';

type SingleNodeSelection = {
  node: TextNode | ElementNode;
  point: Point;
};

const getSingleNodeSelection = (
  selection: RangeSelection,
): SingleNodeSelection | null => {
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();

  if (anchorNode === focusNode) {
    return { node: anchorNode, point: selection.anchor };
  } else {
    return null;
  }
};

const getTargetListItemNode = (
  singleNodeSelection: SingleNodeSelection,
): ListItemNode | null => {
  const { node, point } = singleNodeSelection;

  if ($isListItemNode(node)) {
    return node;
  }

  const parent = node.getParent();

  if (!parent) {
    return null;
  }
  if (!$isListItemNode(parent)) {
    return null;
  }
  if (point.offset === 0) {
    return parent;
  }

  return null;
};

const ListBreakPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }

          const singleNodeSelection = getSingleNodeSelection(selection);
          if (!singleNodeSelection) {
            return false;
          }

          const targetListItemNode = getTargetListItemNode(singleNodeSelection);
          if (!targetListItemNode) {
            return false;
          }

          const paragraphNode = $createParagraphNode();
          targetListItemNode.replace(paragraphNode, true);

          if (paragraphNode.getChildrenSize() === 0) {
            const textNode = $createTextNode('');
            paragraphNode.append(textNode);
            textNode.select();
          }

          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
};

export { ListBreakPlugin };
